import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle } from 'lucide-react';

const MAILCHIMP_FORM_ACTION =
  'https://marvinenergy.us14.list-manage.com/subscribe/post?u=63b23cd577cb17273d728dc1f&id=98c88ae98a&f_id=0071a7e1f0';
const POPUP_NAME = 'mailchimp_subscribe';
const POPUP_SPECS = 'width=620,height=580,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no';

const NewsletterSection = () => {
  const [showThankYou, setShowThankYou] = useState(false);
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const canSubmit = email.trim().length > 0 && consent;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    const form = formRef.current;
    if (!form) return;
    const popup = window.open('', POPUP_NAME, POPUP_SPECS);
    if (popup) {
      form.target = POPUP_NAME;
      form.submit();
      popup.focus();
    } else {
      form.target = '_blank';
      form.submit();
    }
    setShowThankYou(true);
    form.reset();
    setEmail('');
    setConsent(false);
  };

  return (
    <section
      id="newsletter"
      className="py-16 lg:py-20 bg-primary-secondary border-t border-white/10"
      aria-label="Newsletter signup"
    >
      <div className="container mx-auto px-6">
        <motion.div
          className="max-w-xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl lg:text-3xl font-display font-bold text-white mb-2">
            Subscribe
          </h2>
          <p className="text-gray-400 text-sm mb-8">
            Get updates on energy insights and Marvin AI news.
          </p>

          <form
            ref={formRef}
            action={MAILCHIMP_FORM_ACTION}
            method="post"
            id="mc-embedded-subscribe-form"
            name="mc-embedded-subscribe-form"
            className="validate relative"
            onSubmit={handleSubmit}
            noValidate
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <label htmlFor="mce-EMAIL" className="sr-only">
                Email Address
              </label>
              <input
                type="email"
                name="EMAIL"
                id="mce-EMAIL"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 min-w-0 px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-cyan focus:border-primary-cyan/50 transition-colors"
                aria-required="true"
              />
              <button
                type="submit"
                name="subscribe"
                id="mc-embedded-subscribe"
                disabled={!canSubmit}
                className="px-6 py-3 rounded-lg bg-primary-cyan text-primary-bg font-semibold hover:bg-primary-cyan/90 focus:outline-none focus:ring-2 focus:ring-primary-cyan focus:ring-offset-2 focus:ring-offset-primary-secondary transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-cyan"
              >
                Subscribe
              </button>
            </div>

            <div className="mt-4 text-left">
              <label className="flex gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-primary-cyan focus:ring-primary-cyan focus:ring-offset-0"
                  aria-required="true"
                />
                <span className="text-gray-400 text-sm leading-relaxed">
                  I explicitly consent to electronic marketing communications and the related processing of my personal information. Therefore, I explicitly consent to receiving newsletters, event invites and other business-related information from Marvin AI Solutions Kft. I am aware that this constitutes the processing of my personal information in line with the content of the{' '}
                  <Link to="/privacy" className="text-primary-cyan hover:text-primary-cyan/80 underline">
                    privacy notice
                  </Link>
                  , further, that I might withdraw my consent at any time. If you have any questions regarding the above, please contact us at{' '}
                  <a href="mailto:info@marvinenergy.com" className="text-primary-cyan hover:text-primary-cyan/80 underline">
                    info@marvinenergy.com
                  </a>
                  .
                </span>
              </label>
            </div>
            {/* Honeypot for MailChimp - must be present and empty */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-5000px' }}>
              <input
                type="text"
                name="b_63b23cd577cb17273d728dc1f_98c88ae98a"
                tabIndex={-1}
                defaultValue=""
                aria-hidden="true"
              />
            </div>
          </form>

          <p className="text-gray-500 text-xs mt-3">
            <span className="text-gray-400">*</span> indicates required
          </p>
        </motion.div>
      </div>

      {/* Thank you popup */}
      <AnimatePresence>
        {showThankYou && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setShowThankYou(false)}
              aria-hidden
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="thank-you-title"
              className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 px-4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <div className="relative rounded-2xl bg-primary-secondary border border-white/10 shadow-xl p-6 text-center">
                <button
                  type="button"
                  onClick={() => setShowThankYou(false)}
                  className="absolute right-3 top-3 p-1 rounded text-gray-400 hover:text-white transition-colors"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
                <div className="mb-4 flex justify-center">
                  <CheckCircle className="text-primary-cyan" size={48} />
                </div>
                <h3 id="thank-you-title" className="text-xl font-display font-bold text-white mb-2">
                  Thank you for subscribing
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Please check your inbox and confirm your email address to receive our updates.
                </p>
                <button
                  type="button"
                  onClick={() => setShowThankYou(false)}
                  className="px-5 py-2.5 rounded-lg bg-primary-cyan text-primary-bg font-semibold hover:bg-primary-cyan/90 transition-colors"
                >
                  OK
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
};

export default NewsletterSection;
